import { useCallback, useEffect, useRef, useState } from "react";
import {
  getCountFromServer,
  getDocs,
  limit as fsLimit,
  startAfter,
  type DocumentData,
  type Query,
  type QueryDocumentSnapshot,
} from "firebase/firestore";

import { errorText } from "../lib/errors";

/**
 * İMLEÇ TABANLI SAYFALAMA.
 *
 * ── NİÇİN SAYFA NUMARASI YOK ───────────────────────────────────────────────
 * Firestore "1000. kaydın 25 tanesini ver" diyemez; `offset` desteklemez.
 * Sayfa numarası sunmak için her sayfanın imlecini önceden bilmek gerekir ki
 * bu, N sayfa için N sorgu demektir. Gerçek admin panellerinin çoğu (Shopify,
 * Stripe, GitHub) aynı sebeple Önceki/Sonraki kullanır. Sayfa numarası
 * uydurmak, tıklandığında yanlış kayıtları göstermekten iyi değildir.
 *
 * ── TOPLAM SAYI AYRI ÖLÇÜLÜR ───────────────────────────────────────────────
 * `getCountFromServer` dokümanları indirmeden sayar. "100 kayıt gösteriliyor
 * (en fazla 100)" demek, kullanıcıya kaç kayıt OLDUĞUNU söylemez — 101 mi var
 * 10.000 mi, bilinmez. Toplam sayı gösterilmeden sayfalama yarım kalır.
 *
 * ── CANLI DİNLEME YOK ──────────────────────────────────────────────────────
 * Sayfalı liste `getDocs` ile çekilir, `onSnapshot` ile değil: kullanıcı 3.
 * sayfadayken araya yeni kayıt girerse satırlar kayar ve seçim yanlış kayda
 * denk gelir. Sayfalı listede kararlılık, anındalıktan önemlidir. `refresh()`
 * elle tazeleme sağlar.
 */

export interface PagedState<T> {
  docs: Array<T & { id: string }>;
  loading: boolean;
  error: string | null;
  /** Sunucudan sayılan TOPLAM kayıt (sayfa değil). */
  total: number | null;
  /** 1'den başlar. */
  page: number;
  pageSize: number;
  hasNext: boolean;
  hasPrev: boolean;
  next: () => void;
  prev: () => void;
  first: () => void;
  setPageSize: (n: number) => void;
  refresh: () => void;
  /** Bu sayfadaki kayıtların kimlikleri — "tümünü seç" için. */
  pageIds: string[];
}

export const PAGE_SIZES = [25, 50, 100, 250] as const;

export function usePagedQuery<T = DocumentData>(
  /** Sıralaması ve süzgeçleri hazır sorgu. `null` ise sorgu açılmaz. */
  build: () => Query<DocumentData> | null,
  deps: readonly unknown[],
  initialPageSize = 50,
): PagedState<T> {
  const [docs, setDocs] = useState<Array<T & { id: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(initialPageSize);
  const [hasNext, setHasNext] = useState(false);
  const [nonce, setNonce] = useState(0);

  /** Sayfa başlangıç imleçleri: cursors[0] = 1. sayfa (imleçsiz). */
  const cursors = useRef<Array<QueryDocumentSnapshot<DocumentData> | null>>([null]);
  const buildRef = useRef(build);
  buildRef.current = build;

  // Süzgeç/sıralama değişince sayfalama BAŞA döner; aksi hâlde eski imleç yeni
  // sorguya uygulanır ve sonuç sessizce anlamsız olur.
  useEffect(() => {
    cursors.current = [null];
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, pageSize]);

  useEffect(() => {
    let cancelled = false;
    const q = buildRef.current();

    if (!q) {
      setDocs([]);
      setLoading(false);
      setTotal(null);
      setHasNext(false);
      return;
    }

    setLoading(true);
    setError(null);

    const cursor = cursors.current[page - 1] ?? null;
    // Bir fazla çekilir: "sonraki sayfa var mı" sorusunun cevabı budur.
    const paged = cursor
      ? [startAfter(cursor), fsLimit(pageSize + 1)]
      : [fsLimit(pageSize + 1)];

    // `Query` üzerine kısıt eklemek için `query()` gerekli; çağıran hazır
    // sorguyu verdiği için burada sarmalanır.
    import("firebase/firestore")
      .then(({ query }) => getDocs(query(q, ...paged)))
      .then((snap) => {
        if (cancelled) return;
        const all = snap.docs;
        const more = all.length > pageSize;
        const shown = more ? all.slice(0, pageSize) : all;

        // Sonraki sayfanın imleci = bu sayfanın SON gösterilen kaydı.
        if (more && shown.length > 0) {
          cursors.current[page] = shown[shown.length - 1]!;
        }

        setDocs(shown.map((d) => ({ id: d.id, ...(d.data() as T) })));
        setHasNext(more);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(errorText(e, "Liste yüklenemedi."));
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, page, pageSize, nonce]);

  // Toplam sayı ayrı ve sayfadan bağımsız ölçülür.
  useEffect(() => {
    let cancelled = false;
    const q = buildRef.current();
    if (!q) return;

    getCountFromServer(q)
      .then((s) => {
        if (!cancelled) setTotal(s.data().count);
      })
      .catch(() => {
        // Sayım başarısızsa liste yine çalışır; toplam "—" gösterilir.
        if (!cancelled) setTotal(null);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const setPageSize = useCallback((n: number) => setPageSizeState(n), []);

  return {
    docs,
    loading,
    error,
    total,
    page,
    pageSize,
    hasNext,
    hasPrev: page > 1,
    next: () => setPage((p) => (hasNext ? p + 1 : p)),
    prev: () => setPage((p) => Math.max(1, p - 1)),
    first: () => setPage(1),
    setPageSize,
    refresh: () => setNonce((n) => n + 1),
    pageIds: docs.map((d) => d.id),
  };
}
